import { cn } from "@cronika/ui";
import { FC, useRef } from "react";

import { Stepper } from "./Stepper";
import { hmsToSeconds, secondsToHms, type THms } from "./lib";

type THmsStepperProps = {
  value: THms;
  onChange: (next: THms) => void;
  disabled?: boolean;
  className?: string;
  /** Highlights all three plates in the error colour (invalid expiry). */
  invalid?: boolean;
  /** When true (Time mode), clamp a past draft to now + 1 minute on outside click. */
  compareToNow?: boolean;
  labels: { hours: string; minutes: string; seconds: string };
  /** IANA timezone for wall-clock comparisons (falls back to browser local when empty). */
  timezone?: string;
};

/**
 * Row of three {@link Stepper} plates (HOURS / MIN / SEC). Steps carry across
 * fields: decrementing minutes at 4:00 gives 3:59, incrementing seconds at
 * 1:59:59 gives 2:00:00. Time mode wraps within 24 h; Duration mode clamps at 0.
 */
export const HmsStepper: FC<THmsStepperProps> = ({
  value,
  onChange,
  disabled,
  className,
  invalid,
  compareToNow,
  labels,
  timezone = "",
}) => {
  const hoursInputRef = useRef<HTMLInputElement | null>(null);
  const minutesInputRef = useRef<HTMLInputElement | null>(null);
  const secondsInputRef = useRef<HTMLInputElement | null>(null);

  // Direction is set when the user first focuses a field in the current session.
  // 'ltr' = H → M → S → blur; 'rtl' = S → M → H → blur.
  // Minutes inherit the current direction (don't reset it).
  const directionRef = useRef<"ltr" | "rtl">("ltr");

  const focusAndSelect = (ref: React.MutableRefObject<HTMLInputElement | null>) => {
    const el = ref.current;
    if (!el) return;
    el.focus();
    el.select();
  };

  const advanceHours = () => {
    if (directionRef.current === "ltr") focusAndSelect(minutesInputRef);
    else hoursInputRef.current?.blur();
  };
  const advanceMinutes = () => {
    if (directionRef.current === "ltr") focusAndSelect(secondsInputRef);
    else focusAndSelect(hoursInputRef);
  };
  const advanceSeconds = () => {
    if (directionRef.current === "rtl") focusAndSelect(minutesInputRef);
    else secondsInputRef.current?.blur();
  };

  const step = (field: keyof THms, delta: number) => {
    const fieldSecs = field === "hours" ? 3600 : field === "minutes" ? 60 : 1;
    const next = hmsToSeconds(value) + delta * fieldSecs;
    if (compareToNow) {
      onChange(secondsToHms(((next % 86400) + 86400) % 86400));
    } else {
      onChange(secondsToHms(Math.max(0, next)));
    }
  };

  const setField = (field: keyof THms, next: number) => {
    onChange({ ...value, [field]: next });
  };

  return (
    <div className={cn("grid grid-cols-3 gap-2", className)}>
      <Stepper
        value={value.hours}
        label={labels.hours}
        max={24}
        disabled={disabled}
        invalid={invalid}
        compareToNow={compareToNow}
        hms={value}
        field="hours"
        onReplace={onChange}
        onIncrement={() => step("hours", 1)}
        onDecrement={() => step("hours", -1)}
        onChange={(next) => setField("hours", next)}
        timezone={timezone}
        inputRef={hoursInputRef}
        onFocusField={() => { directionRef.current = "ltr"; }}
        onAutoAdvance={advanceHours}
      />
      <Stepper
        value={value.minutes}
        label={labels.minutes}
        max={60}
        disabled={disabled}
        invalid={invalid}
        compareToNow={compareToNow}
        hms={value}
        field="minutes"
        onReplace={onChange}
        onIncrement={() => step("minutes", 1)}
        onDecrement={() => step("minutes", -1)}
        onChange={(next) => setField("minutes", next)}
        timezone={timezone}
        inputRef={minutesInputRef}
        onAutoAdvance={advanceMinutes}
      />
      <Stepper
        value={value.seconds}
        label={labels.seconds}
        max={60}
        disabled={disabled}
        invalid={invalid}
        compareToNow={compareToNow}
        hms={value}
        field="seconds"
        onReplace={onChange}
        onIncrement={() => step("seconds", 1)}
        onDecrement={() => step("seconds", -1)}
        onChange={(next) => setField("seconds", next)}
        timezone={timezone}
        inputRef={secondsInputRef}
        onFocusField={() => { directionRef.current = "rtl"; }}
        onAutoAdvance={advanceSeconds}
      />
    </div>
  );
};
